import { AnySchema, ObjectSchema } from 'yup';
import React, {
  ChangeEvent,
  createContext,
  FC,
  FormHTMLAttributes,
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Awaitable } from '@idk-web/core-utils';

type Form<T extends Record<string, unknown>> = {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  getFormProps(): FormHTMLAttributes<never>;
  getFieldProps(
    field: keyof T,
    fieldType?: HTMLInputTypeAttribute | 'select',
  ): InputHTMLAttributes<never>;
  setTouched(field: keyof T, value: boolean): void;
  setError(field: keyof T, error: string | undefined): void;
  setErrors(errors: Partial<Record<keyof T, string>>): void;
  setValue<U extends keyof T>(
    field: U,
    value: T[U],
    shouldValidate?: boolean,
  ): void;
  setValues(values: Partial<T>, shouldValidate?: boolean): void;
  submit(): void;
  reset(): void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = createContext<Form<any> | null>(null);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const FormProvider: FC<PropsWithChildren<{ form: Form<any> }>> = ({
  form,
  children,
}) => {
  return <FormContext.Provider value={form}>{children}</FormContext.Provider>;
};

type Options<T extends Record<string, unknown>> = {
  initialValues: T;
  validation?: Partial<{
    [K in keyof T]: (value: T[K]) => Awaitable<string | undefined>;
  }>;
  onSubmit?(values: T): Awaitable<void>;
};

/**
 * A hook for managing form state and validation.
 */
export function useForm<T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validation,
}: Options<T>): Form<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateTouched = useCallback((field: keyof T, value: boolean) => {
    setTouched((prev) => {
      if (prev[field] === value) {
        return prev;
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  }, []);

  const updateError = useCallback(
    (field: keyof T, error: string | undefined) => {
      setErrors((prev) => {
        if (prev[field] === error) {
          return prev;
        }

        return {
          ...prev,
          [field]: error,
        };
      });
    },
    [],
  );

  const updateErrors = useCallback(
    (errors: Partial<Record<keyof T, string>>) => {
      setErrors((prev) => {
        if (Object.keys(errors).every((key) => errors[key] === prev[key])) {
          return prev;
        }

        return {
          ...prev,
          ...errors,
        };
      });
    },
    [],
  );

  const validate = useCallback(
    async (values: Partial<T>) => {
      if (validation) {
        const promises = Object.entries(values).map(
          async ([name, value]: [keyof T, T[keyof T]]) => {
            const validate = validation[name];

            if (validate) {
              const error = await validate(value);

              return [name, error] as const;
            }

            return [name, undefined] as const;
          },
        );

        const errors = await Promise.all(promises);

        updateErrors(
          Object.fromEntries(errors) as Partial<Record<keyof T, string>>,
        );

        return Object.keys(errors).length === 0;
      }

      return true;
    },
    [validation, updateErrors],
  );

  const updateValue = useCallback(
    <U extends keyof T>(field: U, value: T[U], shouldValidate = true) => {
      setValues((prev) => {
        if (prev[field] === value) {
          return prev;
        }

        return {
          ...prev,
          [field]: value,
        };
      });

      if (shouldValidate) {
        void validate({ [field]: value } as unknown as Partial<T>);
      }
    },
    [validate],
  );

  const updateValues = useCallback(
    (values: Partial<T>, shouldValidate = true) => {
      setValues((prev) => {
        if (Object.keys(values).every((key) => values[key] === prev[key])) {
          return prev;
        }

        return {
          ...prev,
          ...values,
        };
      });

      if (shouldValidate) {
        void validate(values);
      }
    },
    [validate],
  );

  const submit = useCallback(async () => {
    if (!(await validate(values))) {
      return;
    }

    if (!onSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, values, validate]);

  const getFormProps = useCallback((): FormHTMLAttributes<never> => {
    return {
      onSubmit(e) {
        e.preventDefault();

        void submit();
      },
    };
  }, [submit]);

  const getFieldProps = useCallback(
    (
      field: keyof T,
      fieldType: HTMLInputTypeAttribute | 'select',
    ): InputHTMLAttributes<never> => {
      const props = {
        name: String(field),
        value: values[field],
        type: fieldType,
        onChange(e) {
          const { name, value, type } = e.target;
          const fieldValue =
            type === 'checkbox'
              ? (e.target as HTMLInputElement).checked
              : value;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updateValue(name, fieldValue as any, true);
        },
        onBlur(e) {
          const { name } = e.target;

          updateTouched(name, true);
        },
      } as InputHTMLAttributes<HTMLInputElement | HTMLSelectElement>;

      if (fieldType === 'checkbox') {
        return {
          ...props,
          checked: Boolean(props.value),
        };
      }

      return props;
    },
    [values, updateValue, updateTouched],
  );

  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    submit,
    getFormProps,
    getFieldProps,
    setTouched: updateTouched,
    setError: updateError,
    setErrors: updateErrors,
    setValue: updateValue,
    setValues: updateValues,
    reset,
  };
}

type Field<T> = {
  name: string;
  value: T;
  error?: string;
  touched: boolean;
  handleBlur(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
  handleChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;
  getFieldProps(): InputHTMLAttributes<never>;
  setTouched(value: boolean): void;
  setValue(value: T, shouldValidate?: boolean): void;
};

export function useField<T = unknown>(
  name: string,
  fieldType: HTMLInputTypeAttribute | 'select',
): Field<T> {
  const form = useContext(FormContext);

  if (!form) {
    throw new Error('useField must be used within a FormProvider');
  }

  if (!(name in form.values)) {
    throw new Error(`Field "${name}" does not exist in form`);
  }

  return useMemo(
    () => ({
      name,
      value: form.values[name] as T,
      error: form.errors[name],
      touched: form.touched[name] ?? false,
      handleBlur(e) {
        const { name } = e.target;

        form.setTouched(name, true);
      },
      handleChange(e) {
        const { name, value, type } = e.target;
        const fieldValue =
          type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setValue(name, fieldValue as any, true);
      },
      getFieldProps() {
        return form.getFieldProps(name, fieldType);
      },
      setTouched(value) {
        form.setTouched(name, value);
      },
      setValue(value, shouldValidate = true) {
        form.setValue(name, value, shouldValidate);
      },
    }),
    [name, form.values[name], form.errors[name], form.touched[name]],
  );
}

export function yupAdapter<T extends Record<string, unknown>>(
  schema: ObjectSchema<T>,
): Partial<{
  [K in keyof T]: (value: T[K]) => Awaitable<string | undefined>;
}> {
  return Object.entries(schema.fields).reduce(
    (acc, [name, field]: [keyof T, AnySchema]) => ({
      ...acc,
      [name]: async (value: T[typeof name]) => {
        try {
          await field.validate(value, {
            strict: true,
            abortEarly: false,
          });

          return undefined;
        } catch (error) {
          if (error instanceof Error) {
            return error.message;
          }

          return String(error);
        }
      },
    }),
    {},
  );
}

type YupOptions<T extends Record<string, unknown>> = {
  initialValues: T;
  validationSchema?: ObjectSchema<T>;
  onSubmit?(values: T): Awaitable<void>;
};

export function useYupForm<T extends Record<string, unknown>>({
  initialValues,
  onSubmit,
  validationSchema,
}: YupOptions<T>): Form<T> {
  const validation = useMemo(
    () => (validationSchema ? yupAdapter(validationSchema) : undefined),
    [validationSchema],
  );

  return useForm({
    initialValues,
    onSubmit,
    validation,
  });
}
